import React, { useMemo, useCallback } from 'react';
import { MANAGEABLE_ACCESS_ROUTES, getManageableRoutesGrouped } from '../lib/roles';
import './ProfileTabAccessFields.css';

const pathLabel = (path) =>
  MANAGEABLE_ACCESS_ROUTES.find((r) => r.path === path)?.label || path;

/**
 * Controlled fields for profiles.allowed_tab_paths (custom sidebar sections).
 * @param {{ useCustom: boolean, paths: string[] }} value
 * @param {(next: { useCustom: boolean, paths: string[] }) => void} onChange
 */
export default function ProfileTabAccessFields({
  value,
  onChange,
  idPrefix = 'tab-access',
  disabled = false,
  /** Use outside `estimates-form-grid` (e.g. member details page): vertical stack instead of `display:contents`. */
  variant = 'grid',
}) {
  const { useCustom, paths } = value;

  const pathSortOrder = useMemo(() => {
    const m = new Map();
    MANAGEABLE_ACCESS_ROUTES.forEach((r, i) => m.set(r.path, i));
    return m;
  }, []);

  const sortPaths = useCallback(
    (ps) =>
      [...ps].sort((a, b) => (pathSortOrder.get(a) ?? 999) - (pathSortOrder.get(b) ?? 999)),
    [pathSortOrder],
  );

  const groupedForDropdown = useMemo(() => getManageableRoutesGrouped(), []);

  const setMode = (e) => {
    const custom = e.target.value === 'custom';
    if (disabled) return;
    onChange({ useCustom: custom, paths: custom ? paths : [] });
  };

  const addPath = (path) => {
    if (!path || disabled || paths.includes(path)) return;
    onChange({ useCustom: true, paths: [...paths, path] });
  };

  const removePath = (path) => {
    if (disabled) return;
    onChange({ useCustom: true, paths: paths.filter((p) => p !== path) });
  };

  const remainingSections = groupedForDropdown
    .map((section) => ({
      ...section,
      routes: section.routes.filter((r) => !paths.includes(r.path)),
    }))
    .filter((section) => section.routes.length > 0);

  const sortedPaths = sortPaths(paths);

  const rootClass =
    variant === 'stacked' ? 'profile-tab-access profile-tab-access--stacked' : 'profile-tab-access';

  return (
    <div className={rootClass}>
      <div className="profile-tab-access-title">Sidebar &amp; tabs</div>
      <p className="profile-tab-access-lede">
        By default, navigation follows the user&apos;s <strong>role</strong>. Choose <strong>Custom</strong> to limit
        which sections appear (Dashboard and Settings stay available).
      </p>

      <label className="estimates-form-label profile-tab-access-label" htmlFor={`${idPrefix}-mode`}>
        Access mode
      </label>
      <select
        id={`${idPrefix}-mode`}
        className="estimates-form-select profile-tab-access-select"
        value={useCustom ? 'custom' : 'legacy'}
        onChange={setMode}
        disabled={disabled}
      >
        <option value="legacy">Use role defaults</option>
        <option value="custom">Custom — pick sections below</option>
      </select>

      {useCustom && (
        <>
          <label className="estimates-form-label profile-tab-access-label" htmlFor={`${idPrefix}-add`}>
            Add section
          </label>
          {remainingSections.length > 0 ? (
            <select
              id={`${idPrefix}-add`}
              className="estimates-form-select profile-tab-access-select profile-tab-access-add"
              value=""
              aria-label="Add a section"
              disabled={disabled}
              onChange={(e) => {
                const v = e.target.value;
                if (v) addPath(v);
                e.target.value = '';
              }}
            >
              <option value="">Choose a section to add…</option>
              {remainingSections.map((section) => (
                <optgroup key={section.id} label={section.label}>
                  {section.routes.map((r) => (
                    <option key={r.path} value={r.path}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          ) : (
            <p className="profile-tab-access-hint profile-tab-access-hint-ok">All available sections are included.</p>
          )}

          <div className="profile-tab-access-tags-wrap">
            <span className="estimates-form-label profile-tab-access-label">Allowed sections</span>
            {sortedPaths.length > 0 ? (
              <ul className="profile-tab-access-tag-list" aria-label="Selected sections">
                {sortedPaths.map((path) => (
                  <li key={path} className="profile-tab-access-tag">
                    <span className="profile-tab-access-tag-text">{pathLabel(path)}</span>
                    <button
                      type="button"
                      className="profile-tab-access-tag-remove"
                      onClick={() => removePath(path)}
                      disabled={disabled}
                      aria-label={`Remove ${pathLabel(path)}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="profile-tab-access-hint">
                No extra sections — only Dashboard and Settings until you add sections above.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
